import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { BREAKPOINTS } from "web/react/constants";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { useIntersectionObserver } from "web/react/hooks/use-intersection-observer/use-intersection-observer";
import withReduxProvider from "web/react/redux-provider";
import { ReduxStoreState } from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import pushToGptCmd from "web/script/embedded/google-display-network";
import globals from "web/script/modules/globals";
import listenForEstimatedClicks from "web/script/utils/ad-analytics";
import styles from "./adsense-display.module.css";

declare global {
    interface Window {
        googletag: any;
    }
}

const DEFAULT_INTERSECTION_OBSERVER_OPTIONS = {
    rootMargin: "0px",
    once: true,
};

interface AdsenseDisplayProps {
    divId: string;
    slotId: string;
    usesMobileSize: boolean;
}

export function AdsenseDisplay({
    divId,
    slotId,
    usesMobileSize,
}: AdsenseDisplayProps): JSX.Element | null {
    // https://developers.google.com/publisher-tag/reference
    const tagManager = globals.window.googletag;

    // cookie consent
    const personalizedAdsDirty = useSelector(
        (state: ReduxStoreState): boolean => state.cookieConsentReducer.acceptsAds
    );
    // as final insurance, don't accidentally send non-booleans to analytics customData
    const personalizedAds = personalizedAdsDirty ? true : false;
    // 0 for personalized ads, 1 for non-personalized ads.
    const nonPersonalizedAds = personalizedAds ? 0 : 1;

    // event tracking
    const divRef = useRef<HTMLDivElement>(null);
    const eventCategory = "adsense-display";
    const eventLabel = divId;

    // states
    const { isDesktopViewport } = useDomViewport();
    const [isLoaded, setIsLoaded] = useState(false);
    const [isDisplayed, setIsDisplayed] = useState(false);
    const [isVisible] = useIntersectionObserver(
        DEFAULT_INTERSECTION_OBSERVER_OPTIONS,
        divRef as any
    );

    function addSizes(): [Record<string, any>, string] {
        const mapping = tagManager.sizeMapping();
        const desktopSize = [970, 250];
        const mobileSize = [300, 250];
        let defaultSize;

        if (usesMobileSize) {
            defaultSize = mobileSize;
            mapping.addSize([BREAKPOINTS.Large, BREAKPOINTS.Medium], desktopSize);
            mapping.addSize([0, 0], mobileSize);
        } else {
            defaultSize = desktopSize;
            mapping.addSize([0, 0], desktopSize);
        }

        return [mapping.build(), defaultSize];
    }

    function initTag(): void {
        let loadSucceeded = false;

        function onLoadStart(): void {
            setTimeout(function () {
                if (!loadSucceeded) {
                    analytics.event(eventCategory, "no_ad", eventLabel, true, {
                        personalizedAds,
                    });
                }
            }, 8000);
        }

        function onLoaded(): void {
            loadSucceeded = true;

            listenForEstimatedClicks(divRef.current, eventCategory, eventLabel, {
                personalizedAds,
            });

            analytics.event(eventCategory, "impression", eventLabel, true, {
                personalizedAds,
            });
        }

        // https://developers.google.com/publisher-tag/reference#googletag.pubadsservice
        const service = tagManager.pubads();
        service.setRequestNonPersonalizedAds(nonPersonalizedAds);

        // https://developers.google.com/publisher-tag/reference#googletag.sizemappingbuilder
        const [mapping, defaultSize] = addSizes();

        // https://developers.google.com/publisher-tag/reference#googletag.slot
        const slot = tagManager.defineSlot(slotId, defaultSize, divId);

        service.addEventListener("slotResponseReceived", onLoadStart);
        service.addEventListener("impressionViewable", onLoaded);

        slot.addService(service);
        slot.defineSizeMapping(mapping);

        tagManager.enableServices();
    }

    function displayTag(): void {
        tagManager.display(divId);
    }

    useEffect(() => {
        // Push requests to async command array once if we can.
        // https://developers.google.com/publisher-tag/reference#googletag.cmd
        if (!isLoaded && (usesMobileSize || isDesktopViewport)) {
            pushToGptCmd(initTag);

            setIsLoaded(true);
        }

        if (isVisible && !isDisplayed && (usesMobileSize || isDesktopViewport)) {
            pushToGptCmd(displayTag);

            setIsDisplayed(true);

            analytics.event(eventCategory, "display_ad_loaded", eventLabel, true, {
                personalizedAds,
            });
        }
        // This hook should only run if isVisible && (usesMobileSize || isDesktopViewport) may now be true.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usesMobileSize, isDesktopViewport, isVisible]);

    if (usesMobileSize || isDesktopViewport) {
        return <div ref={divRef} id={divId} className={styles.container}></div>;
    }
    return null;
}

export default withReduxProvider(AdsenseDisplay);
